import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    modelNumber: string;
    itemNumber: string;
    customerProductNumber?: string;
    upc?: string;
    showLabel?: boolean;
    showModelNumber?: boolean;
    showItemNumber?: boolean;
    showCpn?: boolean;
    showUpc?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    modelGridItem?: GridItemProps;
    modelLabelText?: TypographyPresentationProps;
    modelValueText?: TypographyPresentationProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    upcGridItem?: GridItemProps;
    upcLabelText?: TypographyPresentationProps;
    upcValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 5 },
    modelLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    modelValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            margin-left: 2px;
            ${wrapInContainerStyles}
        `,
    },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            margin-left: 2px;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            margin-left: 2px;
            ${wrapInContainerStyles}
        `,
    },
    upcLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    upcValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            margin-left: 2px;
            ${wrapInContainerStyles}
        `,
    },
};

const XcProductPartNumbers: FC<Props> = ({
    modelNumber,
    itemNumber,
    customerProductNumber,
    upc,
    showLabel = true,
    showModelNumber = true,
    showItemNumber = true,
    showCpn = true,
    showUpc = true,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayModel = showModelNumber && modelNumber;
    const displayItemNumber = showItemNumber && itemNumber;
    const displayCustomerNumber = showCpn && customerProductNumber;
    const displayUpc = showUpc && upc;
    const numberOfItems =
        (displayItemNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0) + (displayModel ? 1 : 0) + (displayUpc ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {displayModel && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.modelGridItem}>
                    {showLabel && <Typography {...styles.modelLabelText}>{translate("Model:")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.modelLabelText}>{translate("Model:")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.modelValueText} data-test-selector="model">
                        {modelNumber}
                    </Typography>
                </GridItem>
            )}
            {displayItemNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Item:")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Item:")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="itemNumber">
                        {itemNumber}
                    </Typography>
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #:")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #:")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </GridItem>
            )}
            {displayUpc && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.upcGridItem}>
                    {showLabel && <Typography {...styles.upcLabelText}>{translate("UPC:")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.upcLabelText}>{translate("UPC:")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.upcValueText} data-test-selector="upc">
                        {upc}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default XcProductPartNumbers;
